export const FIELDS = [
  {
    slug: "nurses",
    name: "Nurses",
    title: "Resume Help for Nurses: Bullet Rewriting | Verbulat",
    description: "Nursing resume bullet rewriting: patient load, quality outcomes and clinical keywords written as clear achievements with real numbers. Word + PDF.",
    h1: "Resume help for nurses.",
    intro: "Nursing resumes often read like a list of duties every nurse performs. We rewrite your bullets to show your patient load, the outcomes you influenced and the clinical skills the next unit is hiring for.",
    focus: ["Patient ratios and caseload", "Quality and safety outcomes", "Specialty skills and certifications in context", "Charting, EHR and protocol keywords"],
    examples: [
      { before: "Provided care to patients on a med-surg floor.", after: "Managed care for 5–6 med-surg patients per shift, coordinating with physicians and case managers on discharge plans." },
      { before: "Helped train new nurses.", after: "Precepted 8 new graduate nurses through 12-week orientation, all of whom completed it on schedule." },
    ],
  },
  {
    slug: "software-engineers",
    name: "Software Engineers",
    title: "Resume Help for Software Engineers | Verbulat",
    description: "Software engineer resume bullet rewriting: shipped features, performance gains and tech-stack keywords as measurable achievements. Word + PDF delivery.",
    h1: "Resume help for software engineers.",
    intro: "Engineering resumes tend to list technologies instead of impact. We rewrite your bullets around what you built, what it changed and the stack your target roles ask for.",
    focus: ["Features shipped and who used them", "Performance, reliability and cost improvements", "Scale: users, requests, data volume", "Tech-stack keywords matched to postings"],
    examples: [
      { before: "Worked on the backend API using Python.", after: "Built 14 REST endpoints in Python/FastAPI powering the mobile checkout, used by ~30K monthly customers." },
      { before: "Improved page load speed.", after: "Cut dashboard load time from 4.2s to 1.1s by adding query caching and paginating heavy tables." },
    ],
  },
  {
    slug: "teachers",
    name: "Teachers",
    title: "Resume Help for Teachers: Bullet Rewriting | Verbulat",
    description: "Teacher resume bullet rewriting for classroom and career-change roles: student outcomes, programs led and keywords for your target job. Word + PDF.",
    h1: "Resume help for teachers.",
    intro: "Whether you're moving schools or moving into training, instructional design or another field, we turn classroom work into clear achievements with real numbers.",
    focus: ["Class sizes and student outcomes", "Curriculum and programs you built", "Leadership: committees, mentoring, parent engagement", "Keywords for education or career-change roles"],
    examples: [
      { before: "Taught 8th grade math.", after: "Taught 8th grade math to 120 students across 5 sections, raising the share meeting state benchmarks from 58% to 71%." },
      { before: "Ran the after-school club.", after: "Founded an after-school robotics club that grew from 6 to 28 members and placed 3rd at the district competition." },
    ],
  },
  {
    slug: "sales",
    name: "Sales Professionals",
    title: "Resume Help for Sales Professionals | Verbulat",
    description: "Sales resume bullet rewriting: quota attainment, pipeline, deal size and CRM keywords written as credible, measurable achievements. Word + PDF.",
    h1: "Resume help for sales professionals.",
    intro: "Sales resumes live and die by numbers, and hiring managers can tell when they're padded. We help you present real quota, pipeline and deal results clearly and credibly.",
    focus: ["Quota attainment and rankings", "Pipeline built and deals closed", "Accounts, territories and deal sizes", "CRM and sales-methodology keywords"],
    examples: [
      { before: "Responsible for selling to new clients.", after: "Closed 22 new mid-market accounts in FY25 for $640K in first-year revenue, reaching 112% of quota." },
      { before: "Used Salesforce to track leads.", after: "Rebuilt the team's Salesforce lead stages, cutting stale opportunities by a third within one quarter." },
    ],
  },
  {
    slug: "project-managers",
    name: "Project Managers",
    title: "Resume Help for Project Managers | Verbulat",
    description: "Project manager resume bullet rewriting: delivery on time and budget, team size, scope and PM keywords written as measurable achievements. Word + PDF.",
    h1: "Resume help for project managers.",
    intro: "Project manager resumes often say “managed projects” without showing scale or results. We rewrite your bullets around what you delivered, for whom, and how it landed against time and budget.",
    focus: ["Budget, timeline and scope delivered", "Team size and stakeholders managed", "Risks caught and processes improved", "Methodology and tool keywords (Agile, Jira, PMP)"],
    examples: [
      { before: "Managed multiple projects for the company.", after: "Delivered 6 cross-functional software rollouts in 2025 for a combined $1.2M budget, all within 5% of plan." },
      { before: "Ran weekly status meetings.", after: "Replaced 3 weekly status meetings with a shared Jira dashboard, freeing about 4 hours per week for a 12-person team." },
    ],
  },
  {
    slug: "customer-service",
    name: "Customer Service",
    title: "Resume Help for Customer Service Roles | Verbulat",
    description: "Customer service resume bullet rewriting: ticket volume, response times, satisfaction scores and support-tool keywords as real achievements. Word + PDF.",
    h1: "Resume help for customer service.",
    intro: "Customer service work is full of measurable wins that rarely make it onto the resume. We turn volume, speed and satisfaction into clear bullets that help you move up or move on.",
    focus: ["Ticket, call or chat volume", "Response and resolution times", "Satisfaction scores and escalations handled", "Support-tool keywords (Zendesk, Salesforce, CRM)"],
    examples: [
      { before: "Answered customer calls and emails.", after: "Handled 60+ calls and emails a day in Zendesk while keeping a 94% customer satisfaction score." },
      { before: "Helped train new team members.", after: "Wrote a 20-page support playbook used to onboard 15 new agents, shortening ramp-up from 4 weeks to 2." },
    ],
  },
  {
    slug: "accountants",
    name: "Accountants",
    title: "Resume Help for Accountants: Bullet Rewriting | Verbulat",
    description: "Accountant resume bullet rewriting: close timelines, accuracy, savings found and accounting-software keywords as measurable achievements. Word + PDF.",
    h1: "Resume help for accountants.",
    intro: "Accounting resumes can read like a list of ledgers. We rewrite your bullets to show speed, accuracy and the money your work protected or saved.",
    focus: ["Month-end close speed and accuracy", "Accounts, entities and volume handled", "Savings, recoveries and audit results", "Software keywords (QuickBooks, NetSuite, Excel, GAAP)"],
    examples: [
      { before: "Did month-end close.", after: "Cut month-end close from 10 to 6 business days for 3 entities by automating reconciliations in Excel." },
      { before: "Reviewed vendor invoices.", after: "Reviewed 400+ vendor invoices a month and flagged $27K in duplicate payments for recovery." },
    ],
  },
  {
    slug: "career-changers",
    name: "Career Changers",
    title: "Resume Help for Career Changers | Verbulat",
    description: "Career change resume bullet rewriting: translate past experience into the skills and keywords of your new field, with real numbers. Word + PDF.",
    h1: "Resume help for career changers.",
    intro: "Changing fields means your experience needs translating. We rewrite your bullets so a hiring manager in your new field sees the transferable skills, results and keywords they care about.",
    focus: ["Transferable skills, named in the new field's language", "Results that matter to your target role", "Keywords from target job postings", "What to cut, shorten or move up"],
    examples: [
      { before: "Taught high school English for 7 years.", after: "Designed and delivered 7 years of curriculum for 150+ learners a year, a strong fit for instructional design roles." },
      { before: "Worked as a restaurant manager.", after: "Managed a 25-person team and $1.8M annual budget, cutting food costs 12%, experience that carries into operations roles." },
    ],
  },
];

export const findField = (slug) => FIELDS.find((f) => f.slug === slug);
